/* User preferences: country, locale, theme, and the selected service area.

   Area lives here rather than in a query because it is an input to almost
   every catalog read — the provider list, the slot grid and the delivery fee
   all depend on it — and a value that many things read should have exactly one
   owner.

   Country sits above area: it decides which districts exist, which currency
   prices are counted in and which payment rails appear at checkout. HexClean
   serves Saudi Arabia only today, so the setter is currently a choice with one
   answer — but it is a real, persisted preference rather than a constant,
   because that is the difference between shipping in one market and being
   welded to it. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { load, save } from '../lib/storage'
import { commitLocale, isRtl, prepareLocale, type Locale } from '../i18n'
import {
  DEFAULT_COUNTRY,
  getCountry,
  type CountryCode,
  type CountryDef,
} from '../lib/geo'

export type ThemeChoice = 'light' | 'dark' | 'auto'

/** Where a language change has got to.
 *
 *  `covering` — the veil is coming down and the bundle is being fetched.
 *  `revealing` — the strings and the direction have both changed underneath,
 *  and the veil is being drawn aside.
 *
 *  A flag rather than a boolean because the two halves are not symmetrical:
 *  the first waits on work, the second waits only on the clock. */
export type LocaleSwapPhase = 'idle' | 'covering' | 'revealing'

export interface LocaleSwapState {
  phase: LocaleSwapPhase
  /** The language being moved to. Held through `revealing` so the veil can
   *  keep naming it while it withdraws. */
  to: Locale | null
}

interface PrefsApi {
  locale: Locale
  setLocale: (l: Locale) => void
  localeSwap: LocaleSwapState
  theme: ThemeChoice
  setTheme: (t: ThemeChoice) => void
  /** The resolved theme after `auto` is applied. Components that need to draw
   *  a theme-dependent asset read this, never `theme`. */
  resolvedTheme: 'light' | 'dark'
  area: string
  setArea: (a: string) => void
  country: CountryCode
  setCountry: (c: CountryCode) => void
  /** The full definition for the active country — currency, dial code,
   *  payments, districts. Read this rather than re-deriving from the code. */
  countryDef: CountryDef
  isRtl: boolean
}

const PrefsContext = createContext<PrefsApi | null>(null)

const THEME_KEY = 'theme'
const AREA_KEY = 'area'
const COUNTRY_KEY = 'country'

/* How long the veil takes to come down, and the beat it holds once it has.
   The cover has to finish before the swap lands or the user watches the page
   mirror itself; the hold exists because a veil that covers and uncovers in
   one continuous motion reads as a flicker rather than as a deliberate step.
   Kept in sync with components/region/LocaleSwap, which owns the drawing. */
export const LOCALE_COVER_MS = 200
export const LOCALE_HOLD_MS = 70
export const LOCALE_REVEAL_MS = 460

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/* The <meta name="theme-color"> value drives the iOS status bar and the
   Android address bar. It has to track the theme or the chrome around the app
   stays white while the app goes black. */
function paintBrowserChrome(mode: 'light' | 'dark') {
  const meta = document.querySelector('meta[name="theme-color"]')
  /* The top of the page, not the canvas underneath it — this paints the strip
     directly above the header, so it has to match `--wash-1` rather than
     `--canvas`. It said `#f2f2f7` back when the backdrop was iOS grey; against
     the mint wash that was a visible grey band above a mint header. */
  if (meta) meta.setAttribute('content', mode === 'dark' ? '#04322c' : '#e6f8f5')
}

function applyTheme(choice: ThemeChoice): 'light' | 'dark' {
  const system = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  const resolved = choice === 'auto' ? system : choice

  /* Always stamp the *resolved* value, including for `auto`.

     This used to remove the attribute under `auto` and leave the CSS media
     query in charge — but design/tokens.css defines the dark palette only
     under `:root[data-theme='dark']`, with no `prefers-color-scheme` block
     behind it. With `auto` as the default theme, that meant every visitor on a
     dark device got the light palette, after a boot screen that had already
     gone black from index.html's own media query.

     Stamping keeps one source of truth for the palette (the attribute) and
     costs nothing, because the effect below already subscribes to the media
     query and re-runs this whenever the OS flips. */
  document.documentElement.setAttribute('data-theme', resolved)
  paintBrowserChrome(resolved)
  return resolved
}

export function PrefsProvider({
  initialLocale,
  children,
}: {
  initialLocale: Locale
  children: ReactNode
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)
  /* Light by default, not `auto` — the same decision the partner app makes and
     documents. This app is read outdoors in Riyadh and light is the legible
     theme in direct sun; following a phone that flipped itself dark at 6pm
     hands someone the wrong theme for the hour they are actually using it.
     `auto` remains available, and dark is good — both are *chosen*. */
  const [theme, setThemeState] = useState<ThemeChoice>(() => load<ThemeChoice>(THEME_KEY, 'light'))
  const [resolvedTheme, setResolved] = useState<'light' | 'dark'>('light')
  /* Empty until either the customer picks one or the served list arrives and
     `useResolvedArea` adopts the first of them.
     ───────────────────────────────────────────────────────────────────────
     It used to default to a district named in this bundle, which is a guess
     about the server's data dressed up as a preference: the day the launch
     market moved, every returning visitor's app opened on a district nobody
     serves and showed them an empty storefront with no hint why. Which
     districts exist is the API's answer, so the default is too. */
  const [area, setAreaState] = useState<string>(() => load<string>(AREA_KEY, ''))
  const [country, setCountryState] = useState<CountryCode>(() =>
    load<CountryCode>(COUNTRY_KEY, DEFAULT_COUNTRY),
  )
  const [swap, setSwap] = useState<LocaleSwapState>({ phase: 'idle', to: null })

  useEffect(() => {
    setResolved(applyTheme(theme))
    if (theme !== 'auto') return
    // Only `auto` needs to listen; an explicit choice is not the OS's business.
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setResolved(applyTheme('auto'))
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])

  const setTheme = useCallback((next: ThemeChoice) => {
    save(THEME_KEY, next)
    setThemeState(next)
  }, [])

  const setArea = useCallback((next: string) => {
    save(AREA_KEY, next)
    setAreaState(next)
  }, [])

  /* Changing country invalidates the selected district — it belongs to the
     country you just left. Cleared rather than swapped for that market's first
     bundled district: `useResolvedArea` fills it from the served list, and a
     guess made here would be a district the API might not have. */
  const setCountry = useCallback((next: CountryCode) => {
    save(COUNTRY_KEY, next)
    setCountryState(next)
    save(AREA_KEY, '')
    setAreaState('')
  }, [])

  /* Changing language is the one preference that rebuilds the entire screen:
     every string is replaced and, between English and Arabic, the layout is
     mirrored. Done bare, the user watches a page reflow — text jumps sides,
     images swap corners, and anything mid-animation restarts. That reads as a
     glitch, not as a setting taking effect.

     So the swap happens behind a veil. The cover and the bundle fetch run
     together rather than in sequence: on a warm cache the bundle is already
     there and the animation is the only cost, and on a cold one the download
     hides inside a beat that was going to be spent anyway.

     `busy` is a ref rather than state because its only job is to reject a
     second press mid-swap, and reading it must not wait for a render. */
  const swapBusy = useRef(false)

  const setLocale = useCallback(
    (next: Locale) => {
      if (next === locale || swapBusy.current) return
      swapBusy.current = true

      /* Someone who has asked for less motion still needs the change to
         happen — they just should not be shown a curtain to explain it. The
         veil renders as a plain, brief crossfade at these durations. */
      const reduce = prefersReducedMotion()
      const cover = reduce ? 90 : LOCALE_COVER_MS
      const hold = reduce ? 0 : LOCALE_HOLD_MS
      const reveal = reduce ? 90 : LOCALE_REVEAL_MS

      const run = async () => {
        setSwap({ phase: 'covering', to: next })

        /* Download and cover run together, and *neither* is allowed to change
           anything on screen. The commit is the only visible step and it
           happens once both are done — the bundle load is allowed to overrun
           the curtain, because a slightly longer veil is cheaper than a frame
           of Arabic in a left-to-right layout. */
        await Promise.all([prepareLocale(next), wait(cover)])
        await commitLocale(next)
        setLocaleState(next)

        await wait(hold)
        setSwap({ phase: 'revealing', to: next })
        await wait(reveal)
        setSwap({ phase: 'idle', to: null })
        swapBusy.current = false
      }

      void run().catch(() => {
        /* The bundle failed to download. Leaving the veil up would strand the
           user behind an opaque screen for a locale they never got, so it
           comes down and the language simply stays as it was. */
        setSwap({ phase: 'idle', to: null })
        swapBusy.current = false
      })
    },
    [locale],
  )

  const value = useMemo<PrefsApi>(
    () => ({
      locale,
      setLocale,
      localeSwap: swap,
      theme,
      setTheme,
      resolvedTheme,
      area,
      setArea,
      country,
      setCountry,
      countryDef: getCountry(country),
      isRtl: isRtl(locale),
    }),
    [locale, setLocale, swap, theme, setTheme, resolvedTheme, area, setArea, country, setCountry],
  )

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>
}

export function usePrefs(): PrefsApi {
  const ctx = useContext(PrefsContext)
  if (!ctx) throw new Error('usePrefs must be used inside <PrefsProvider>')
  return ctx
}
